/* eslint-disable no-param-reassign */
/* eslint-disable no-case-declarations */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable prefer-destructuring */
/* eslint-disable max-len */
import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { Button } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { messages as platformMessages } from 'gw-platform-translations';
import { renderNode, objectFormStatePath } from './ElementMapping';
import SignDocumentComponent from '../../SignDocumentComponent/SignDocumentComponent';
import styles from './AdacConfigurableForm.module.scss';
import messages from './AdacConfigurableForm.messages';
import { parseJSONStringToObject, validateDateField, validateTextField, getLabelForElement } from './ElementMappingUtil';
import { _ } from 'core-js';


/**
 * Applies the applier function recursively for each element as well as its children.
 */
const applyRecursively = (applier, getChildren) => {
    const fn = elements =>  Array.from(elements)?.forEach(elem => {
        applier(elem)

        const children = getChildren(elem)
        if (children) {
            fn(children)
        }
    })

    return fn
}

const AdacConfigurableForm = ({
    sendButtonClick,
    htmlTemplate,
    isSendingDocument,
    handleError,
    validationOutcome,
    setValidationOutcome
}) => {
    const [isDocumentSigning, setIsDocumentSigning] = useState(false);
    const [isDocumentSigned, setIsDocumentSigned] = useState(false);
    const [formElements, setFormElements] = useState([]); // Base for render
    const [formData, setFormData] = useState({});
    const [validHtmlTemplate, setValidHtmlTemplate] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(true);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [formInputs, setFormInputs] = useState({}); // Tree for validations.

    const canvasRef = useRef({});
    const viewModelService = useContext(ViewModelServiceContext);

    const translator = useTranslator();

    const addAttributesToNodes = (node) => {
        if (node.nodeType === 1) {
            // Add a unique ID
            node.setAttribute('uniqueId', Math.random().toString(36).substr(2, 9));

            // Add a label to node if one exists
            if (['input', 'textarea', 'select'].includes(node.tagName.toLowerCase())) {
                const label = getLabelForElement(node);
                if (label) {
                    node.setAttribute('label', label);
                }
            }
        }

        Array.from(node.children).forEach(addAttributesToNodes);
    };

    const extractNodes = useCallback((element, node) => {
        // Assumption is that every element that has 'name' tag should be considered as valid authentication node.
        const elementName = element.getAttribute('name');
        let parentNodeForSubnodes;
        if (elementName !== undefined && elementName !== null) {
            parentNodeForSubnodes = {
                elementType: element.tagName,
                name: elementName,
                dataBindValidations: element.getAttribute('data-bind-validation'),
                dataType: element.getAttribute('data-bind-type'),
                required: element.hasAttribute('required'),
                validateDate: element.getAttribute('validateDate'),
                validation: element.getAttribute('validation')
            };

            if (_.isEmpty(node.children)) {
                node.children = [parentNodeForSubnodes];
            } else {
                node.children.push(parentNodeForSubnodes);
            }
        } else {
            parentNodeForSubnodes = node;
        }

        if (!_.isEmpty(element.children)) {
            Array.from(element.children).forEach((subNode) => extractNodes(subNode, parentNodeForSubnodes));
        }
    }, []);

    const generateInputsTree = useCallback((form) => {
        const rootNode = {};

        Array.from(form.children).forEach((child) => extractNodes(child, rootNode));
        return rootNode;
        // Dependencies are left empty because we only want to render it once, we don't want to recalculate as template should never change.
    }, []);

    useEffect(() => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlTemplate, 'text/html');
            const form = doc.getElementById('form');

            if (!form) {
                throw new Error('Form not found in the template');
            }

            addAttributesToNodes(form);

            // Form Inputs are used for validation only.
            // Validations never change so it's sufficent to render it only once.
            setFormInputs(generateInputsTree(form));

            // Form elements are base for layout creation
            setFormElements(Array.from(form.children));
        } catch (error) {
            handleError(messages.errorReadingTemplate, true);
            setValidHtmlTemplate(false);
        }
    }, [htmlTemplate, handleError]);

    const initFormValues = (element, formState, parentPath) => {
        // Correct paths for fields being part of object/array
        const formStatePath = objectFormStatePath(element, parentPath);

        // Populate formState with initial value if the element has a value
        if (element.value && !(formStatePath in formState)) {
            // Checkbox requires special handling
            if (element.type === 'checkbox' && element.checked === true) {
                handleChange(formStatePath, true);
            } else if (element.type === 'checkbox' && element.value === 'on') {
                handleChange(formStatePath, false);
            } else {
                handleChange(formStatePath, element.value);
            }
        }
    }


    // Initialize form values
    useEffect(() => applyRecursively(element => initFormValues(
        element,
        formData,
        undefined
    ), e => e.children)(formElements), [formElements]);

    const applyAnnotationValidations = useCallback((currentFormData, childElement, validationMessages) => {
        const validationAttributes = childElement.dataBindValidations;
        if (!_.isEmpty(validationAttributes)) {
            const currentValue = currentFormData[childElement.name];

            // Sample tag value: data-bind-validation="@Required(),@Size(0,20)"
            // We split by "," this is followed by "@" to not break parameters. Here we would have "[@Required(), @Size(0,20)]"
            const annotations = validationAttributes.split(new RegExp(',(?=@)'));
            annotations.forEach((entry) => {
                const openingBracketIndex = entry.indexOf('(');
                const annotationName = entry.substring(1, openingBracketIndex);
                const annotationParametersString = entry.substring(openingBracketIndex + 1, entry.length - 1);

                const parameters = {};
                if (!_.isEmpty(annotationParametersString)) {
                    switch (annotationName) {
                        case 'Pattern':
                            // Pattern needs separate treating in order to not break the regex.
                            const patternAnnotationParameters = annotationParametersString.split("','");
                            parameters.firstParameter = patternAnnotationParameters[0].substr(1);
                            parameters.secondParameter = patternAnnotationParameters[1].substr(0, patternAnnotationParameters[1].length - 1);
                            break;
                        default:
                            const defaultAnnotationParameters = annotationParametersString.split(',');
                            // Currently all our annotations have 0, 1 or 2 parameters.
                            // If it has elements it's means there is at least 1 param.

                            parameters.firstParameter = defaultAnnotationParameters[0];
                            if (defaultAnnotationParameters.length === 2) {
                                parameters.secondParameter = defaultAnnotationParameters[1];
                            }
                    }
                }

                // Initialization of the DTO is needed to use OOTB Expressions validation mechanism
                // This allows us to have consistant validation on frontend and backend.
                // For more details take a look into AnnotationDTO in CC.
                const nodeVM = viewModelService.create(
                    {
                        data: currentValue,
                        ...parameters
                    },
                    'cc',
                    `adac.edge.capabilities.claim.documentSign.annotationdtos.${annotationName}DTO`
                );

                const isValid = nodeVM.data.aspects.valid;

                if (isValid === false) {
                    validationMessages.push(nodeVM.data.aspects.validationMessage);
                }
            });
        }
    }, [viewModelService]);

    const applyBackendValidations = useCallback((validationMessages, backendValidation) => {
        // Backend validations are more of an precaution from manual request manipulation.
        // Theoretically should be aligned with Frontend validations as both rely on data-bind-validation tag.
        // Nevertheless, they will be shown around the fields

        Object.entries(backendValidation.validationErrors).forEach((entry) => {
            const key = entry[0];
            const value = entry[1];

            if (!_.isEmpty(value)) {
                // Adjustment is done for arrays and propagating error messages to proper children of the array.
                // Similar thing is done in ElementMapping class so is you change logic here, change it also there.
                const adjustedKey = key.replace('[', '.children[');
                _.set(validationMessages, `${adjustedKey}.validationMessages`, value);
            }
        });
    }, []);

    // Validates if objects/array is empty, it is, has not null properties.
    // Required as we need to initialize table/objects to not fail on render.
    const isDeepEmpty = (obj) => {
        if (obj == null || _.isEmpty(obj)) return true;

        if (typeof obj !== 'object') return false;

        if (Array.isArray(obj)) {
            return obj.every(isDeepEmpty);
        }

        return Object.keys(obj).every((key) => isDeepEmpty(obj[key]));
    };

    const applyDataBindTypeValidations = useCallback((currentValue, childElement, validationMessages) => {
        switch (childElement.dataType) {
            case 'integer':
                if (currentValue !== undefined && !_.isEmpty(currentValue)) {
                    const numberValue = Number(currentValue);
                    if (Number.isNaN(numberValue)) {
                        validationMessages.push(translator(platformMessages.notANumber));
                    } else {
                        const isInteger = Number.isInteger(numberValue);
                        if (!isInteger) {
                            validationMessages.push(translator(platformMessages.notAnInteger));
                        }
                    }
                }
                break;
            default:
                // Nothing needs to be done here.
        }
    }, [translator]);

    const validateNode = useCallback((nodeUnderValidation, currentFormData, backendValidation) => {
        const nodeValidationObject = {};
        const nodeValidationMessages = [];

        switch (nodeUnderValidation.elementType) {
            case 'DIV':
                // DIV is considered/understood as Object.
                // On top level we only validate whether object is required (If is missing in current form data)
                const isObjectMissing = isDeepEmpty(currentFormData[nodeUnderValidation.name]);
                if (isObjectMissing) {
                    const isRequiredObject = nodeUnderValidation.required;
                    if (isRequiredObject) {
                        nodeValidationMessages.push(translator(messages.requiredFieldValidationMessage));
                    }
                // We only validate object fields (subNodes) if the object is present, not empty and there is definition(childrens of node).
                } else if (!_.isEmpty(nodeUnderValidation.children)) {
                    nodeUnderValidation.children.forEach((element) => {
                        nodeValidationObject[element.name] = validateNode(element, currentFormData[nodeUnderValidation.name], backendValidation);
                    });
                }
                break;
            case 'TABLE':
                // Table is considered/understood as Container for array of objects.
                // On top level we only validate whether table is required (If is missing in current form data)
                const isTableEmpty = isDeepEmpty(currentFormData[nodeUnderValidation.name]);
                if (isTableEmpty) {
                    const isRequiredTable = nodeUnderValidation.required;
                    if (isRequiredTable) {
                        nodeValidationMessages.push(translator(messages.requiredFieldValidationMessage));
                    }
                // We only validate object fields (subNodes) if the object is present, not empty and there is definition(childrens of node).
                } else if (!_.isEmpty(nodeUnderValidation.children)) {
                    const arrayElements = currentFormData[nodeUnderValidation.name];
                    if (!_.isEmpty(arrayElements)) {
                        // Table node contains definition of object within.
                        // So to maintain validations for each entry within array in form data, we need to introduce a children field.
                        nodeValidationObject.children = [];
                        arrayElements.forEach((element) => {
                            const newArrayElement = {};
                            nodeUnderValidation.children.forEach((childNode) => {
                                newArrayElement[childNode.name] = validateNode(childNode, element, backendValidation);
                            });
                            nodeValidationObject.children.push(newArrayElement);
                        });
                    }
                }
                break;
            default:
                // Default are all simple input fields, it is: input, checkbox, select, etc...
                if (nodeUnderValidation.required && !currentFormData[nodeUnderValidation.name]) {
                    nodeValidationMessages.push(translator(messages.requiredFieldValidationMessage));
                }

                if (nodeUnderValidation.elementType.toLowerCase() === 'input'
                    && nodeUnderValidation.type === 'date'
                    && currentFormData[nodeUnderValidation.name]) {
                    const dateValue = currentFormData[nodeUnderValidation.name];
                    const validationRules = parseJSONStringToObject(nodeUnderValidation.validateDate || '{}');
                    const validationMessage = validateDateField(
                        dateValue,
                        validationRules,
                        nodeUnderValidation.required,
                        translator
                    );

                    if (validationMessage !== null) {
                        nodeValidationMessages.push(validationMessage);
                    }
                }

                if (nodeUnderValidation.elementType.toLowerCase() === 'input'
                    && nodeUnderValidation.type === 'text'
                    && currentFormData[nodeUnderValidation.name]) {
                    const currentValue = currentFormData[nodeUnderValidation.name];
                    const validationRules = parseJSONStringToObject(nodeUnderValidation.validation || '{}');
                    const validationMessage = validateTextField(
                        currentValue,
                        validationRules,
                        nodeUnderValidation.required,
                        translator
                    );

                    if (validationMessage !== null) {
                        nodeValidationMessages.push(validationMessage);
                    }
                }

                applyDataBindTypeValidations(currentFormData[nodeUnderValidation.name], nodeUnderValidation, nodeValidationMessages);
                applyAnnotationValidations(currentFormData, nodeUnderValidation, nodeValidationMessages);
        }
        nodeValidationObject.validationMessages = nodeValidationMessages;
        return nodeValidationObject;
    }, [applyAnnotationValidations, applyDataBindTypeValidations, translator]);

    // Does a recursive check on the passed object to check if any of field (or field fields) has value in property 'validationMessages
    function hasValidationMessages(obj) {
        let found = false;

        function recursiveValidationCheck(item) {
            if (_.isArray(item)) {
                item.forEach(recursiveValidationCheck);
            } else if (_.isObject(item)) {
                if (_.has(item, 'validationMessages')
                    && Array.isArray(item.validationMessages)
                    && item.validationMessages.length > 0) {
                    found = true;
                }
                Object.values(item).forEach(recursiveValidationCheck);
            }
        }

        recursiveValidationCheck(obj);
        return found;
    }

    const validateForm = useCallback((currentFormData, backendValidation) => {
        const validationsTreeRoot = {};

        formInputs.children.forEach((childElement) => {
            validationsTreeRoot[childElement.name] = validateNode(childElement, currentFormData, backendValidation);
        });

        if (!_.isEmpty(backendValidation)) {
            applyBackendValidations(validationsTreeRoot, backendValidation);
        }

        setValidationErrors(validationsTreeRoot);

        const isFormValidLocal = !hasValidationMessages(validationsTreeRoot);
        setIsFormValid(isFormValidLocal);
        return isFormValidLocal;
    }, [formInputs.children, validateNode, applyBackendValidations]);

    useEffect(() => {
        if (validationOutcome.initialValidation === true) {
            validateForm(formData, validationOutcome);
        }
    }, [validateForm, validationOutcome]);

    const handleChange = useCallback((name, value) => {
        const backendValidation = _.clone(validationOutcome);
        if (!_.isEmpty(backendValidation) && backendValidation.validationErrors[name] !== undefined) {
            backendValidation.validationErrors[name] = [];
            backendValidation.initialValidation = false;
        }

        // Clear out validation paths for entire objects as well.
        if (!_.isEmpty(backendValidation) && name.indexOf('.') !== -1) {
            const parts = name.split('.');

            // Generate all parent paths including the full path
            const parentPaths = parts
                .slice(0, -1) // remove the last part
                .map((_, i) => parts.slice(0, i + 1).join('.'));

            parentPaths.forEach((path) => {
                if (backendValidation.validationErrors[path]) {
                    backendValidation.validationErrors[path] = [];
                }
            });
        }

        setValidationOutcome(backendValidation);

        setFormData((prevState) => {
            const updatedFormState = {
                ...prevState
            };
            _.set(updatedFormState, name, value);

            if (hasSubmitted) {
                validateForm(updatedFormState, backendValidation);
            }

            return updatedFormState;
        });
    }, [setFormData, validationOutcome, setValidationOutcome, hasSubmitted, validateForm]);

    const renderForm = useCallback(() => {
        try {
            return formElements.map((element) => (
                <div key={element}>
                    {Array.from(element.children)
                        .map((child) => 
                            renderNode(
                                child,
                                formData,
                                handleChange,
                                undefined,
                                validationErrors,
                                !isFormValid
                            )
                        )
                    }
                </div>
            ));
        } catch (error) {
            handleError(messages.errorReadingTemplate, true);
            return null;
        }
    }, [formElements, formData, handleChange, handleError, validationErrors, isFormValid]);

    const handleFormSubmit = (event) => {
        event.preventDefault();

        const formValid = validateForm(formData, validationOutcome);
        setHasSubmitted(true);

        if (formValid) {
            setIsDocumentSigning(true);
        }
    };

    const handleSubmitDocument = (event) => {
        event.preventDefault();

        const formValid = validateForm(formData, validationOutcome);
        setHasSubmitted(true);

        if (formValid) {
            // Format any Date objects in formData to 'dd.MM.yyyy' format to
            // ensure consistent date representation before initiating the document signing process.
            const formattedFormData = { ...formData };
            Object.keys(formattedFormData).forEach((field) => {
                if (formattedFormData[field] instanceof Date) {
                    formattedFormData[field] = formattedFormData[field].toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                }
            });
            setIsDocumentSigning(true);

            sendButtonClick(canvasRef, formattedFormData);
        }
    };

    return (
        <div>
            {renderForm()}
            {!isFormValid && (
                <div className={styles.error}>
                    <span>{translator(messages.errorMandatoryFields)}</span>
                </div>
            )}
            {!isDocumentSigning && validHtmlTemplate && (
                <div className={styles.buttonContainer}>
                    <Button onClick={handleFormSubmit}>
                        {translator(messages.signButton)}
                    </Button>
                </div>
            )}
            {isDocumentSigning && (
                <SignDocumentComponent
                    canvasRef={canvasRef}
                    isDocumentSigned={isDocumentSigned}
                    setIsDocumentSigned={setIsDocumentSigned}
                    isSendingDocument={isSendingDocument}
                    submitDocumentSign={handleSubmitDocument}
                />
            )}
        </div>
    );
};

export default AdacConfigurableForm;
